import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ContextualLinks } from "@/components/ContextualLinks";
import { cityLinksForTopic, guidesForTopics, servicesForTopics } from "@/lib/internalLinks";

const URL = "https://apexav.com/blog/custom-home-theater-design-guide";
const TITLE = "Custom Home Theater Design Guide: Room, Sightlines & Acoustics";
const DESCRIPTION =
  "A technical custom home theater design guide: room dimensions and ratios, screen size and seating sightlines, speaker layout, acoustic treatment, and prewire.";

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "What is the ideal room size for a custom home theater?",
    a: "Most dedicated theaters work best at roughly 15–20 ft long by 12–16 ft wide with a 9 ft or taller ceiling. Avoid cube-like rooms and exact whole-number ratios (like 1:1 or 2:1) because they stack room modes and make bass uneven.",
  },
  {
    q: "How far should seating be from the screen?",
    a: "For a cinematic 36–40 degree horizontal viewing angle, place the primary row about 1.2–1.5 times the screen width away. A 120-inch diagonal 16:9 screen (about 105 inches wide) puts the main row roughly 11–13 feet back.",
  },
  {
    q: "Do I need acoustic treatment in a home theater?",
    a: "Yes. Treatment is what makes dialogue intelligible and bass even. Plan broadband absorption at first reflection points, bass trapping in corners, and some diffusion on the rear wall rather than covering every surface in foam.",
  },
  {
    q: "How much does a custom home theater cost in Denver?",
    a: "Media room conversions typically start in the low five figures, while a dedicated, acoustically treated theater with riser, projection, and immersive audio is usually a mid-to-high five-figure project. Room construction and prewire drive the range more than the electronics.",
  },
];

export const Route = createFileRoute("/blog/custom-home-theater-design-guide")({
  head: () => ({
    meta: [
      { title: `${TITLE} | Apex AV` },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "custom home theater, home theater design, home theater design near me, home theater room dimensions, home theater seating distance, home theater acoustic treatment, home theater Denver",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: TITLE,
              description: DESCRIPTION,
              datePublished: "2026-08-05",
              dateModified: "2026-08-05",
              author: { "@type": "Organization", name: "Apex AV" },
              publisher: { "@id": "https://apexav.com/#organization" },
              mainEntityOfPage: { "@type": "WebPage", "@id": URL },
              articleSection: "Home Theater",
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: GuidePage,
});

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="mt-14 font-display text-3xl font-bold scroll-mt-24">
      {children}
    </h2>
  );
}

function GuidePage() {
  const topics = ["home-theater", "prewire", "whole-home-audio"] as const;

  return (
    <>
      <article className="container mx-auto max-w-3xl px-6 pt-16 pb-20">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <p className="mt-8 text-sm font-semibold uppercase tracking-wider text-brand-text">
          Home Theater
        </p>
        <h1 className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">
          Custom Home Theater Design Guide
        </h1>
        <p className="mt-5 text-lg text-muted-foreground">
          The engineering side of home theater design — room geometry, screen size and sightlines,
          speaker layout, acoustics, light control, and the prewire decisions you can only make
          once. Written from what we build in Denver-area homes every week.
        </p>

        <nav aria-label="Contents" className="mt-10 rounded-xl border border-border bg-card/40 p-6">
          <h2 className="font-display text-lg font-semibold">What this guide covers</h2>
          <ol className="mt-4 space-y-2 text-sm text-muted-foreground">
            {[
              ["room", "Room dimensions and ratios"],
              ["screen", "Screen size, throw, and viewing angle"],
              ["seating", "Seating sightlines and risers"],
              ["speakers", "Speaker layout and immersive audio"],
              ["acoustics", "Acoustic treatment that actually matters"],
              ["light", "Light control, walls, and finishes"],
              ["hvac", "HVAC, noise floor, and power"],
              ["prewire", "Prewire and equipment location"],
              ["budget", "Where the budget goes"],
              ["faq", "Frequently asked questions"],
            ].map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="text-brand-text underline-offset-4 hover:underline">
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="prose prose-invert mt-4 max-w-none prose-headings:font-display prose-p:leading-relaxed prose-p:text-muted-foreground prose-li:text-muted-foreground prose-a:text-brand-text">
          <H2 id="room">1. Room dimensions and ratios</H2>
          <p>
            Every decision downstream is constrained by the shell. Before picking a projector or a
            speaker, get the geometry right. A rectangular room with the screen on a short wall is
            the easiest room to make sound and look excellent.
          </p>
          <ul>
            <li>
              <strong>Target footprint:</strong> roughly 15–20 ft long by 12–16 ft wide for a
              single- or two-row theater.
            </li>
            <li>
              <strong>Ceiling height:</strong> 9 ft finished minimum if you want overhead height
              channels; 10 ft gives room for a riser plus soffits.
            </li>
            <li>
              <strong>Avoid problem ratios:</strong> square rooms and exact 1:1, 1:2, or 2:1
              relationships pile room modes on top of each other, producing one boomy note and one
              missing note. Slightly irregular ratios (for example 1 : 1.28 : 1.54 height : width :
              length) spread modes out.
            </li>
            <li>
              <strong>Symmetry left to right:</strong> keep the screen wall centered and the side
              walls mirrored. Asymmetry is the most common cause of a soundstage that pulls to one
              side.
            </li>
          </ul>
          <p>
            In a basement, check beam and duct locations before you commit to the screen wall — a
            single soffit in the wrong place can cost you 8 inches of screen width or an entire row
            of overhead speakers.
          </p>

          <H2 id="screen">2. Screen size, throw, and viewing angle</H2>
          <p>
            Size the screen from the seating distance, not the other way around. Reference viewing
            angles land between 36 and 40 degrees horizontally, which works out to roughly 1.2–1.5×
            the screen width for the primary row.
          </p>
          <ul>
            <li>10–11 ft viewing distance → about a 100–110 in diagonal</li>
            <li>12–13 ft viewing distance → about a 120–130 in diagonal</li>
            <li>14–16 ft viewing distance → about a 135–150 in diagonal</li>
          </ul>
          <p>
            Projector vs. direct-view display is a light question first. If the room can be made
            genuinely dark, projection wins on scale and cinema feel. If the space doubles as a
            family room with windows, a large direct-view panel will look better at every hour of
            the day. Confirm throw distance and lens shift against the actual mounting location
            before framing — chasing a projector position after drywall is the single most common
            rework we see.
          </p>

          <H2 id="seating">3. Seating sightlines and risers</H2>
          <p>
            Sightlines are geometry, and they are easy to verify on paper. Draw a line from each
            viewer&apos;s eye height to the bottom of the screen and confirm nothing — heads,
            headrests, or a bar top — crosses it.
          </p>
          <ul>
            <li>
              <strong>Row spacing:</strong> 5–6 ft front to back for recliners once fully extended.
              Measure the reclined depth, not the upright depth.
            </li>
            <li>
              <strong>Riser height:</strong> 8–12 in for a second row at typical distances; taller
              rooms and longer throws need more.
            </li>
            <li>
              <strong>Screen bottom:</strong> keep it 24–36 in off the floor so front-row viewers
              aren&apos;t looking up and back-row viewers still see the full frame.
            </li>
            <li>
              <strong>Off-axis seats:</strong> keep every seat within about 15 degrees of center for
              consistent image geometry and surround imaging. Wide single rows sound worse at the
              ends than a tighter two-row layout.
            </li>
            <li>
              <strong>Build risers hollow and braced,</strong> then treat them — an untreated riser
              cavity resonates and smears bass.
            </li>
          </ul>

          <H2 id="speakers">4. Speaker layout and immersive audio</H2>
          <p>
            Choose the surround format before framing, because the format dictates how many wire
            runs and back boxes you need.
          </p>
          <ul>
            <li>
              <strong>Left, center, right:</strong> identical models, acoustically aligned to ear
              height at the primary row. Behind an acoustically transparent screen is ideal; above
              or below the screen, angle them toward the seats.
            </li>
            <li>
              <strong>Surrounds:</strong> at or slightly above ear height, roughly 90–110 degrees to
              the sides for 5.x layouts; add rear pairs for 7.x.
            </li>
            <li>
              <strong>Height channels:</strong> in-ceiling pairs forward and rearward of the seats
              for a 5.1.4 or 7.1.4 layout. Lay them out to the seating plan, not to the lighting
              plan.
            </li>
            <li>
              <strong>Subwoofers:</strong> two to four, distributed rather than stacked in one
              corner. Multiple subs are the cheapest, most effective way to even out bass across
              every seat.
            </li>
            <li>
              <strong>Wire generously:</strong> 14 AWG (or 12 AWG for long subwoofer and LCR runs),
              plus conduit from the rack to the screen wall for future upgrades.
            </li>
          </ul>

          <H2 id="acoustics">5. Acoustic treatment that actually matters</H2>
          <p>
            Treatment is not decoration, and covering everything in foam makes rooms sound dead and
            lifeless. Treat in this order:
          </p>
          <ol>
            <li>
              <strong>Bass trapping in the corners</strong> — thick, broadband absorbers where the
              walls meet the ceiling and floor. This does more for clarity than anything else.
            </li>
            <li>
              <strong>First reflection points</strong> — 2 in or thicker panels on the side walls
              and ceiling at the mirror points between speakers and seats. This is what makes
              dialogue intelligible.
            </li>
            <li>
              <strong>Rear wall</strong> — absorption or diffusion behind the last row to stop slap
              echo.
            </li>
            <li>
              <strong>Isolation</strong> — decoupled walls, resilient channel, double drywall with a
              damping compound, and solid-core doors with proper seals if the theater shares a wall
              with bedrooms.
            </li>
          </ol>
          <p>
            Finish with measurement. Room correction and a calibrated subwoofer crossover are what
            turn good hardware into a room that sounds right — but correction cannot fix a room
            that was never treated.
          </p>

          <H2 id="light">6. Light control, walls, and finishes</H2>
          <ul>
            <li>Dark, matte finishes on the screen wall, ceiling, and front third of the side walls.</li>
            <li>
              Blackout motorized shades on any window, with side channels or pockets to kill light
              leak at the edges.
            </li>
            <li>
              Dimmable, glare-free lighting on scenes: bright for cleaning, mid for intermission,
              and low-level step and aisle lighting for playback.
            </li>
            <li>Matte or dark equipment faces, and no LEDs or clocks visible from the seats.</li>
          </ul>

          <H2 id="hvac">7. HVAC, noise floor, and power</H2>
          <p>
            A quiet room is a bigger upgrade than most gear. Aim for a noise floor you barely
            notice with the system off.
          </p>
          <ul>
            <li>Oversized, lined supply and return ducts with low air velocity and no direct paths.</li>
            <li>A dedicated zone or thermostat — a theater full of people plus a projector adds real heat load.</li>
            <li>
              Dedicated 20 A circuits for the rack and for subwoofer locations, plus surge
              protection and clean grounding to avoid hum.
            </li>
            <li>Ventilated equipment location — heat is the top killer of AV electronics.</li>
          </ul>

          <H2 id="prewire">8. Prewire and equipment location</H2>
          <p>
            Prewire is cheap before drywall and expensive after. At minimum, plan for speaker runs
            to every planned location including formats you might add later, two Ethernet runs to
            the display and rack, conduit from the rack to the screen wall and to the ceiling,
            control wiring at the entry keypad, and shade power at every window. Put the rack in a
            ventilated closet outside the theater so fan noise never reaches the seats.
          </p>
          <p>
            Our{" "}
            <Link to="/blog/$slug" params={{ slug: "low-voltage-prewire-checklist" }}>
              low-voltage prewire checklist for new construction
            </Link>{" "}
            is the version we hand to builders before framing inspection.
          </p>

          <H2 id="budget">9. Where the budget goes</H2>
          <p>
            Spend on what does not get replaced: the room build, isolation, acoustic treatment,
            in-wall wiring, speakers, and seating. Sources, streamers, and processors will turn over
            two or three times before any of that does. If the budget is tight, build the room
            correctly and prewire fully now, then upgrade electronics later.
          </p>

          <H2 id="faq">Frequently asked questions</H2>
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="mt-8 font-display text-xl font-semibold">{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 rounded-xl border border-border bg-card/40 p-8 text-center">
          <h2 className="font-display text-2xl font-bold">
            Want a design drawn for your actual room?
          </h2>
          <p className="mt-3 text-muted-foreground">
            We&apos;ll measure the space, model sightlines and speaker layout, and give you a plan
            and budget. Free in-home consultations across the Denver Metro and Front Range.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow transition hover:opacity-90"
          >
            Book a Home Theater Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </article>

      <ContextualLinks
        heading="Next steps for your home theater project"
        intro="Services referenced in this guide, related planning guides, and the Denver-area communities where we build theaters most often."
        groups={[
          {
            heading: "Services in this guide",
            icon: "service",
            links: servicesForTopics([...topics], 4),
          },
          {
            heading: "Related guides",
            icon: "guide",
            links: guidesForTopics([...topics], 4, "custom-home-theater-design-guide"),
          },
          {
            heading: "Denver-area service areas",
            icon: "area",
            links: cityLinksForTopic("home-theater", 5),
          },
        ]}
      />
    </>
  );
}
